/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { TrialQuotaNotice } from '@features/subscription';
import Button from '@/shared/ui/components/basic/Button';
import { usePartnershipsWizard, type SearchParams } from '../context/PartnershipsWizardContext';
import { ResultsList } from './ResultsList';
import { SearchProgress } from './SearchProgress';

interface BodyProps {
  agencyId: string;
  isPolling: boolean;
  trialBlocked: boolean;
  onRetry?: () => void;
}

/**
 * What fills the step: progress while polling, the trial block when the free
 * period's searches are spent (RN-07: a retry would be refused again), or the
 * results.
 *
 * @param props - The step's state.
 * @returns The body.
 */
function ResultsBody({ agencyId, isPolling, trialBlocked, onRetry }: BodyProps) {
  const results = usePartnershipsWizard((s) => s.results);
  const loading = usePartnershipsWizard((s) => s.loading);
  const error = usePartnershipsWizard((s) => s.error);
  const searched = usePartnershipsWizard((s) => s.searched);
  const searchStatus = usePartnershipsWizard((s) => s.searchStatus);
  const agenciesFound = usePartnershipsWizard((s) => s.agenciesFound);
  const propertiesRetrieved = usePartnershipsWizard((s) => s.propertiesRetrieved);

  if (isPolling && searchStatus) {
    return (
      <SearchProgress
        status={searchStatus}
        agenciesFound={agenciesFound}
        propertiesRetrieved={propertiesRetrieved}
      />
    );
  }
  if (trialBlocked) {
    return <TrialQuotaNotice agencyId={agencyId} kind="partnershipSearch" forceBlock />;
  }
  return (
    <ResultsList
      results={results}
      searched={searched}
      loading={loading}
      error={error}
      onRetry={onRetry}
    />
  );
}

/**
 * Final wizard step that shows live search progress while polling and renders
 * the results once complete.
 *
 * The action row distinguishes two states so the controls are never
 * duplicative: before a search has been performed (`searched === false`) a
 * plain back arrow returns to the filters step; once a real search has run
 * (`searched === true`) the back arrow is replaced by "Refinar", the explicit
 * "adjust the filters of the search you just ran" action (the filters step
 * pre-fills from `lastParams`). "Nova Busca" is the prominent primary action.
 *
 * @returns The results step.
 */
export function ResultsStep() {
  const { t } = useTranslation('features/partnerships');
  const { agency_id } = useParams<{ agency_id: string }>();
  const setStep = usePartnershipsWizard((s) => s.setStep);
  const reset = usePartnershipsWizard((s) => s.reset);
  const trialBlocked = usePartnershipsWizard((s) => s.trialQuotaExhausted);
  const searched = usePartnershipsWizard((s) => s.searched);
  const lastParams = usePartnershipsWizard((s) => s.lastParams);
  const runSearch = usePartnershipsWizard((s) => s.runSearch);
  const searchStatus = usePartnershipsWizard((s) => s.searchStatus);

  const handleBack = () => setStep('filters');
  const handleRefine = () => setStep('filters');
  const handleNewSearch = () => reset();
  const canRetry = Boolean(agency_id && lastParams.city?.trim());
  const handleRetry = canRetry
    ? () => runSearch(lastParams as SearchParams, agency_id!)
    : undefined;

  const isPolling =
    searchStatus === 'pending' || searchStatus === 'running';

  return (
    <div className="flex h-full min-h-0 flex-col gap-[var(--space-4)]">
      <div className="flex items-center gap-[var(--space-3)]">
        {searched ? (
          <Button variant="outlined" onClick={handleRefine}>
            {t('wizard.refine')}
          </Button>
        ) : (
          <Button variant="ghost" onClick={handleBack}>
            ← {t('wizard.back')}
          </Button>
        )}
        <Button variant="solid" onClick={handleNewSearch}>
          {t('wizard.new_search')}
        </Button>
      </div>
      <div className="min-h-0 flex-1">
        <ResultsBody
          agencyId={agency_id ?? ''}
          isPolling={isPolling}
          trialBlocked={trialBlocked}
          onRetry={handleRetry}
        />
      </div>
    </div>
  );
}
